import React, { Fragment, useState, useEffect } from "react";
import Loader from "react-loader-spinner";
import CustomLoader from "../../customLoader/CustomLoader";
import GetAdminService from "../../../services/admin/GetAdminService";
import AppHeader from "../../../Layout/AppHeader";
import AppSidebar from "../../../Layout/AppSidebar";
import "./Statistics.css";

const initialStatistics = {
  activeCounselorsMonth: 0,
  activeSchoolsMonth: 0,
  messageSentMonth: 0,
  activeCounselorsWeek: 0,
  activeSchoolsWeek: 0,
  messageSentWeek: 0,
  numStudents: 0,
  numActiveStudents: 0,
  numOptedInStudents: 0,
};

const formatNumber = (value) => {
  if (value === null || value === undefined || value === "") return "0";
  const num = Number(value);
  if (Number.isNaN(num)) return value;
  return num.toLocaleString();
};

const Statistics = () => {
  const [loadingForCsvDownload, setLoadingForCsvDownload] = useState(false);
  const [loading, setLoading] = useState(false);
  const [statistics, setStatistics] = useState(initialStatistics);

  const downloadFile = (downloadURL, fileName = "statistics.csv") => {
    if (!downloadURL) return;
    const downloadLink = document.createElement("a");
    downloadLink.href = downloadURL;
    downloadLink.download = fileName;
    downloadLink.target = "_blank";
    downloadLink.rel = "noopener noreferrer";
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  };

  const downLoadCsvTemplate = () => {
    setLoadingForCsvDownload(true);
    GetAdminService.getStudentStatisticsExcel()
      .then((data) => {
        const downloadURL = data?.data?.download;
        if (downloadURL) {
          downloadFile(downloadURL, "detailed-statistics.csv");
        }
        setLoadingForCsvDownload(false);
      })
      .catch(() => {
        setLoadingForCsvDownload(false);
      });
  };

  const getStatisticsData = () => {
    setLoading(true);
    GetAdminService.getStudentStatistics()
      .then((data) => {
        setLoading(false);
        setStatistics(data?.data?.response);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    getStatisticsData();
  }, []);

  return (
    <Fragment>
      <AppHeader />
      <div className="app-main">
        <AppSidebar />
        <div className="app-main__outer">
          {loading ? (
            <div className="admin-stats-loading">
              <CustomLoader />
            </div>
          ) : (
            <div className="admin-stats-page">
              <div className="admin-stats-header">
                <div>
                  <h1 className="admin-stats-title">Statistics</h1>
                  <p className="admin-stats-subtitle">
                    Usage statistics for College Connect
                  </p>
                </div>
                <button
                  onClick={() => downLoadCsvTemplate()}
                  className="admin-stats-download-btn"
                  disabled={loadingForCsvDownload}
                >
                  Download Detailed Statistics
                  {loadingForCsvDownload && (
                    <Loader
                      type="TailSpin"
                      color="white"
                      height={18}
                      width={18}
                    />
                  )}
                </button>
              </div>

              <div className="admin-stats-grid">
                {/* Global */}
                <section className="admin-stats-card">
                  <div className="admin-stats-card-header">
                    <h2 className="admin-stats-card-title">Global Statistics</h2>
                    <p className="admin-stats-card-hint">
                      Overall student engagement across the platform
                    </p>
                  </div>

                  <div className="admin-stats-metrics">
                    <div className="admin-stats-metric full">
                      <p className="admin-stats-metric-label">
                        % Students Signed Up
                      </p>
                      <p className="admin-stats-metric-value accent">
                        {statistics?.percentage_active_students || 0}%
                      </p>
                    </div>
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label">
                        Total Number of Students
                      </p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.total_students)}
                      </p>
                    </div>
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label"># Active Students</p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.total_active_students)}
                      </p>
                    </div>
                    <div className="admin-stats-metric full">
                      <p className="admin-stats-metric-label">
                        # Students Opted In
                      </p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.total_opted_in_students)}
                      </p>
                    </div>
                  </div>
                </section>

                {/* Monthly */}
                <section className="admin-stats-card">
                  <div className="admin-stats-card-header">
                    <h2 className="admin-stats-card-title">Monthly Statistics</h2>
                    <p className="admin-stats-card-hint">
                      Statistics over the last 30 days
                    </p>
                  </div>

                  <div className="admin-stats-metrics">
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label">
                        # Active Counselors
                      </p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.active_counselor_last_month)}
                      </p>
                    </div>
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label"># Active Schools</p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.active_school_in_last_month)}
                      </p>
                    </div>
                    <div className="admin-stats-metric full">
                      <p className="admin-stats-metric-label"># Message Sent</p>
                      <p className="admin-stats-metric-value accent">
                        {formatNumber(statistics?.sent_message_in_last_month)}
                      </p>
                    </div>
                  </div>
                </section>

                {/* Weekly */}
                <section className="admin-stats-card">
                  <div className="admin-stats-card-header">
                    <h2 className="admin-stats-card-title">Weekly Statistics</h2>
                    <p className="admin-stats-card-hint">
                      Statistics over the last 7 days
                    </p>
                  </div>

                  <div className="admin-stats-metrics">
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label">
                        # Active Counselors
                      </p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.active_counselor_last_week)}
                      </p>
                    </div>
                    <div className="admin-stats-metric">
                      <p className="admin-stats-metric-label"># Active Schools</p>
                      <p className="admin-stats-metric-value">
                        {formatNumber(statistics?.active_school_in_last_week)}
                      </p>
                    </div>
                    <div className="admin-stats-metric full">
                      <p className="admin-stats-metric-label"># Message Sent</p>
                      <p className="admin-stats-metric-value accent">
                        {formatNumber(statistics?.sent_message_in_last_week)}
                      </p>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          )}
        </div>
      </div>
    </Fragment>
  );
};

export default Statistics;
